// src/components/RfqCreateForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// --- Constants (keep as they are) ---
const commonInputClass = "mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2";
const commonLabelClass = "block text-sm font-medium text-gray-700";
const errorTextClass = "mt-1 text-sm text-red-600";
const formSectionClass = "p-6 bg-white border border-gray-200 rounded-lg shadow-sm";

const COUNTRY_OPTIONS = ["UAE", "KSA", "Kuwait", "Oman", "Qatar", "Bahrain", "Iraq", "Lebanon", "Jordan", "Egypt", "Morocco", "Others"];
const METHODOLOGY_OPTIONS = ["Quantitative", "Qualitative", "Both"];
const DATA_COLLECTION_OPTIONS = ["Face to face", "CATI", "MS", "Online Panel", "Online (Database)", "CLT", "Focus Group Discussions", "In-Depth Interviews", "Others, specify"];
const UAE_CITY_OPTIONS = ["Dubai", "Abu Dhabi City", "Al Ain", "Al Dhafra", "Sharjah", "Northern Emirates", "Not applicable"];

const RfqCreateForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        short_description: '',
        // +++ MODIFIED: ADD DEADLINE TO INITIAL STATE +++
        deadline: '',
        countries: [],
        cities_uae: [],
        methodologies: [],
        data_collection_methods: [],
        target_segment: '',
        recruitment_criteria: '',
        other_requirements: 'N/A',
        project_timeline: '',
        project_budget: 'N/A',
    });
    const [sampleSizes, setSampleSizes] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleMultiSelectChange = (e, field) => {
        const values = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({ ...prev, [field]: values }));

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }

        if (field === 'data_collection_methods') {
            const newSampleSizes = {};
            values.forEach(method => {
                newSampleSizes[method] = sampleSizes[method] || '';
            });
            setSampleSizes(newSampleSizes);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSampleSizeChange = (method, value) => {
        setSampleSizes(prev => ({ ...prev, [method]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "RFQ Name is required.";
        if (!formData.countries || formData.countries.length === 0) newErrors.countries = "At least one country must be selected.";
        if (!formData.methodologies || formData.methodologies.length === 0) newErrors.methodologies = "At least one methodology must be selected.";
        if (!formData.data_collection_methods || formData.data_collection_methods.length === 0) newErrors.data_collection_methods = "At least one data collection method must be selected.";
        if (!formData.target_segment.trim()) newErrors.target_segment = "Target segment definition is required.";
        if (!formData.recruitment_criteria.trim()) newErrors.recruitment_criteria = "Recruitment criteria are required.";
        if (!formData.project_timeline.trim()) newErrors.project_timeline = "Project timeline is required.";
        
        formData.data_collection_methods.forEach(method => {
            if (!sampleSizes[method] || isNaN(parseFloat(sampleSizes[method])) || parseFloat(sampleSizes[method]) <= 0) {
                if (!newErrors.sample_sizes) newErrors.sample_sizes = {};
                newErrors.sample_sizes[method] = `Valid sample size for ${method} is required.`;
            }
        });
        if (newErrors.sample_sizes && Object.keys(newErrors.sample_sizes).length > 0) {
            newErrors.sample_sizes_general = "Please provide valid sample sizes for all selected methods."
        }

        // +++ NEW: ADD DEADLINE VALIDATION +++
        if (formData.deadline) {
            const deadlineDate = new Date(formData.deadline);
            const now = new Date();
            if (deadlineDate < now) {
                newErrors.deadline = "Submission deadline cannot be in the past.";
            }
        }
        // +++ END NEW SECTION +++

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            alert("Please correct the errors in the form.");
            return;
        }

        setIsLoading(true);
        setErrors({});

        // +++ MODIFIED: PREPARE PAYLOAD, HANDLING OPTIONAL DEADLINE +++
        const payload = {
            ...formData,
            sample_sizes: sampleSizes,
        };
        
        // If deadline is empty, remove it so backend receives null, not an empty string.
        if (!payload.deadline) {
            delete payload.deadline;
        }
        // +++ END MODIFIED SECTION +++

        try {
            const response = await api.post('/rfqs/', payload);
            alert('RFQ created successfully!');
            console.log("RFQ creation response:", response.data);
            if (response.data && response.data.id) {
                navigate(`/rfqs/${response.data.id}`);
            } else {
                console.error("RFQ ID not found in response:", response.data);
                alert("RFQ created, but could not retrieve ID for redirection.");
                navigate('/rfqs');
            }
        } catch (err) {
            console.error("Error creating RFQ:", err);
            if (err.response && err.response.data) {
                const backendErrors = err.response.data;
                const formattedErrors = {};
                if (typeof backendErrors === 'object' && backendErrors !== null) {
                    for (const key in backendErrors) {
                        formattedErrors[key] = Array.isArray(backendErrors[key]) ? backendErrors[key].join(' ') : backendErrors[key];
                    }
                } else if (Array.isArray(backendErrors)) {
                     formattedErrors.general = backendErrors.join(' ');
                } else {
                    formattedErrors.general = "An unexpected error occurred. Please try again.";
                }
                setErrors(formattedErrors);
                alert("Failed to create RFQ. Please check the form for errors.");
            } else {
                setErrors({ general: "A network error occurred. Please try again." });
                alert("A network error occurred. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Create New Request for Quotation</h1>
                    <p className="mt-2 text-md text-gray-600">Fill in the details below to request quotes from qualified vendors.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* RFQ Name and Short Description Section (Unchanged) */}
                    <div className={formSectionClass}>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">RFQ Identification</h2>
                        <div>
                            <label htmlFor="name" className={commonLabelClass}>RFQ Name / Project Title*</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className={commonInputClass}
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g., Brand Tracker Wave 12 - UAE"
                            />
                            {errors.name && <p className={errorTextClass}>{errors.name}</p>}
                        </div>
                        <div className="mt-4">
                            <label htmlFor="short_description" className={commonLabelClass}>Short Description</label>
                            <textarea
                                id="short_description"
                                name="short_description"
                                rows="2"
                                className={commonInputClass}
                                value={formData.short_description}
                                onChange={handleInputChange}
                                placeholder="Briefly describe the project or RFQ."
                            ></textarea>
                            {errors.short_description && <p className={errorTextClass}>{errors.short_description}</p>}
                        </div>
                    </div>

                    {/* General Information Section (Unchanged) */}
                    <div className={formSectionClass}>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Scope</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label htmlFor="countries" className={commonLabelClass}>Countries (MA)*</label>
                                <select id="countries" multiple className={commonInputClass} value={formData.countries} onChange={(e) => handleMultiSelectChange(e, 'countries')}>
                                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple.</p>
                                {errors.countries && <p className={errorTextClass}>{errors.countries}</p>}
                            </div>
                            {formData.countries.includes('UAE') && (
                                <div>
                                    <label htmlFor="cities_uae" className={commonLabelClass}>Cities in UAE (MA)</label>
                                    <select id="cities_uae" multiple className={commonInputClass} value={formData.cities_uae} onChange={(e) => handleMultiSelectChange(e, 'cities_uae')}>
                                        {UAE_CITY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    {errors.cities_uae && <p className={errorTextClass}>{errors.cities_uae}</p>}
                                </div>
                            )}
                             <div>
                                <label htmlFor="methodologies" className={commonLabelClass}>Methodology (MA)*</label>
                                <select id="methodologies" multiple className={commonInputClass} value={formData.methodologies} onChange={(e) => handleMultiSelectChange(e, 'methodologies')}>
                                    {METHODOLOGY_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                                {errors.methodologies && <p className={errorTextClass}>{errors.methodologies}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Data Collection & Sample Size Section (Unchanged) */}
                    <div className={formSectionClass}>
                         <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Collection & Sample Sizes</h2>
                         <div>
                            <label htmlFor="data_collection_methods" className={commonLabelClass}>Data Collection Methods (MA)*</label>
                            <select id="data_collection_methods" multiple className={commonInputClass} value={formData.data_collection_methods} onChange={(e) => handleMultiSelectChange(e, 'data_collection_methods')}>
                                {DATA_COLLECTION_OPTIONS.map(dcm => <option key={dcm} value={dcm}>{dcm}</option>)}
                            </select>
                             {errors.data_collection_methods && <p className={errorTextClass}>{errors.data_collection_methods}</p>}
                        </div>
                        {formData.data_collection_methods.length > 0 && (
                            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {formData.data_collection_methods.map(method => (
                                    <div key={method}>
                                        <label htmlFor={`ss_${method}`} className="text-sm font-medium text-gray-700">{`Sample Size for ${method}*`}</label>
                                        <input
                                            type="number"
                                            id={`ss_${method}`}
                                            className={commonInputClass}
                                            value={sampleSizes[method] || ''}
                                            onChange={(e) => handleSampleSizeChange(method, e.target.value)}
                                            placeholder="e.g., 500"
                                        />
                                        {errors.sample_sizes && errors.sample_sizes[method] && <p className={errorTextClass}>{errors.sample_sizes[method]}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                         {errors.sample_sizes_general && <p className={errorTextClass}>{errors.sample_sizes_general}</p>}
                    </div>

                    {/* Project Details Section */}
                    <div className={formSectionClass}>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h2>
                         <div className="space-y-6">
                            {/* ... other project detail fields ... */}
                            <div>
                                <label htmlFor="target_segment" className={commonLabelClass}>Target Segment Definition*</label>
                                <textarea id="target_segment" name="target_segment" rows="3" className={commonInputClass} value={formData.target_segment} onChange={handleInputChange}></textarea>
                                {errors.target_segment && <p className={errorTextClass}>{errors.target_segment}</p>}
                            </div>
                            <div>
                                <label htmlFor="recruitment_criteria" className={commonLabelClass}>Recruitment Criteria*</label>
                                <textarea id="recruitment_criteria" name="recruitment_criteria" rows="3" className={commonInputClass} value={formData.recruitment_criteria} onChange={handleInputChange}></textarea>
                                {errors.recruitment_criteria && <p className={errorTextClass}>{errors.recruitment_criteria}</p>}
                            </div>
                             <div>
                                <label htmlFor="other_requirements" className={commonLabelClass}>Other Requirements</label>
                                <textarea id="other_requirements" name="other_requirements" rows="2" className={commonInputClass} value={formData.other_requirements} onChange={handleInputChange}></textarea>
                            </div>
                             <div>
                                <label htmlFor="project_timeline" className={commonLabelClass}>Project Timeline*</label>
                                <textarea id="project_timeline" name="project_timeline" rows="2" className={commonInputClass} value={formData.project_timeline} onChange={handleInputChange}></textarea>
                                {errors.project_timeline && <p className={errorTextClass}>{errors.project_timeline}</p>}
                            </div>
                             <div>
                                <label htmlFor="project_budget" className={commonLabelClass}>Project Budget</label>
                                <textarea id="project_budget" name="project_budget" rows="2" className={commonInputClass} value={formData.project_budget} onChange={handleInputChange}></textarea>
                            </div>
                            
                            {/* +++ NEW: DEADLINE INPUT FIELD +++ */}
                            <div>
                                <label htmlFor="deadline" className={commonLabelClass}>
                                    Submission Deadline (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    id="deadline"
                                    name="deadline"
                                    className={commonInputClass}
                                    value={formData.deadline}
                                    onChange={handleInputChange}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Vendors cannot submit quotes after this date and time.
                                </p>
                                {errors.deadline && <p className={errorTextClass}>{errors.deadline}</p>}
                            </div>
                            {/* +++ END NEW SECTION +++ */}
                         </div>
                    </div>

                    {/* Submit Button Section (Unchanged) */}
                    <div className="pt-5">
                         {errors.general && <p className={`${errorTextClass} text-center mb-4`}>{errors.general}</p>}
                        <div className="flex justify-end">
                            <button type="button" onClick={() => navigate(-1)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                Cancel
                            </button>
                            <button type="submit" disabled={isLoading} className="ml-3 inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isLoading ? 'Creating...' : 'Create RFQ'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RfqCreateForm;